import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { useAuth } from '@/state/AuthContext'
import { api } from '@/util/api'
import { retryWait } from '@/lib/retryWait'
import { withoutOrganization } from '@/components/auth/isViewer'
import { safeReturnTo } from '@/components/onboarding/authContinuation'
import { useOnboardingAnalytics } from '@/components/onboarding/useOnboardingAnalytics'
import { PublicAuthLayout } from '@/components/public/PublicAuthLayout'
import { Button, ButtonLink, Notice, TextField } from '@/components/play'

/** The 201 answers as signing in does (SessionPayload); the organisation is a JSON:API resource. */
type StartHostingResponse = { data: { token: string; organization?: { attributes?: { slug?: string } } } }
type StartHostingError = {
  response?: { status?: number; data?: { error?: { type?: string; errors?: { name?: string[]; username?: string[] } } } }
}
/**
 * `already-hosting` offers a full page load to the way back, which loads the account and its organisation again;
 * `profile` (a username from before today's rules, which can't be saved again) offers the profile.
 */
type Failure = { kind: 'notice' | 'already-hosting' | 'profile'; message: string }

const NAME_MISSING = 'Name your organization.'
// The server's message (Organization#name_makes_a_slug), so the field reads the same whichever side refuses.
const NAME_NEEDS_A_TO_Z = 'Name needs a letter or number from A to Z'
const FAILED = "We couldn't start hosting. Try again."
const OFFLINE = "We couldn't start hosting. Check your connection and try again."
const ALREADY_HOSTING = 'You already host with an organization.'
// Before anyone types: the name is public, and what the handle is made from (the server refuses the same).
const NAME_RULE = 'Shown on your events. Up to 80 characters, with a letter or number from A to Z.'

/** The organisation's @handle is made from its name's A–Z and 0–9 (Organization.slug_base); a name without any makes none. */
const makesAHandle = (name: string) => /[a-z0-9]/.test(name.toLowerCase())

function failureFrom(err: StartHostingError): Failure | { field: string } {
  const response = err?.response
  if (!response) return { kind: 'notice', message: OFFLINE }
  if (response.status === 429) {
    const wait = retryWait(err)
    return { kind: 'notice', message: `Too many tries. Try again ${wait ? `in ${wait}` : 'later'}.` }
  }
  if (response.status !== 422) return { kind: 'notice', message: FAILED }
  const error = response.data?.error
  if (error?.type === 'already_hosting') return { kind: 'already-hosting', message: ALREADY_HOSTING }
  const nameError = error?.errors?.name?.[0]
  if (nameError) return { field: nameError }
  // The username messages are written as form copy (config/locales/en.yml); other refusals' wording isn't.
  const usernameError = error?.errors?.username?.[0]
  return usernameError ? { kind: 'profile', message: usernameError } : { kind: 'notice', message: FAILED }
}

/**
 * A signed-in viewer names an organisation and becomes its admin, sees the @handle the server made from the name (it
 * can differ: `trail-crew-1` when `trail-crew` is taken), then goes back to the host page that sent them.
 */
export function StartHostingPage() {
  const { organization, user, completeOAuthLogin } = useAuth()
  const track = useOnboardingAnalytics()
  const [searchParams] = useSearchParams()
  const returnTo = safeReturnTo(searchParams.get('return_to'))
  const [name, setName] = useState('')
  const [nameError, setNameError] = useState<string | null>(null)
  const [failure, setFailure] = useState<Failure | null>(null)
  const [busy, setBusy] = useState(false)
  // Set once this page has made the organisation: its handle, or null when the answer didn't name it.
  const [hosting, setHosting] = useState<{ handle: string | null } | null>(null)
  const posting = useRef(false)
  const nameInput = useRef<HTMLInputElement>(null)
  const noticeAction = useRef<HTMLAnchorElement>(null)
  const hostingNotice = useRef<HTMLDivElement>(null)

  // After a refusal, focus goes to the fix: the field, or the notice's way on when the name isn't the problem.
  useEffect(() => {
    if (failure) (noticeAction.current ?? nameInput.current)?.focus()
    else if (nameError) nameInput.current?.focus()
  }, [failure, nameError])

  useEffect(() => {
    if (hosting) hostingNotice.current?.focus()
  }, [hosting])

  // Someone who already hosts carries on to the host page that sent them here. The new session makes this page's own
  // viewer a host too; they stay on their handle until they continue. A manager without an organisation names one
  // here, to create events of their own.
  if (!hosting && !withoutOrganization(organization, user)) return <Navigate to={returnTo} replace />

  function start(event: FormEvent) {
    event.preventDefault()
    if (posting.current) return
    setFailure(null)
    const refusal = !name.trim() ? NAME_MISSING : !makesAHandle(name) ? NAME_NEEDS_A_TO_Z : null
    if (refusal) {
      setNameError(refusal)
      nameInput.current?.focus()
      return
    }
    posting.current = true
    setBusy(true)
    setNameError(null)
    api.post<StartHostingResponse>('/account/organization', { organization: { name } })
      .then((res) => {
        track('hosting_started')
        // The new token carries the organisation; applying it loads the account again before host pages open.
        completeOAuthLogin(res.data.token)
        // The organisation exists now: an answer without its handle still says they're hosting, never a failure.
        setHosting({ handle: res.data.organization?.attributes?.slug || null })
      })
      .catch((err: StartHostingError) => {
        posting.current = false
        setBusy(false)
        const result = failureFrom(err)
        if ('field' in result) setNameError(result.field)
        else setFailure(result)
      })
  }

  const noticeActions: Record<Failure['kind'], ReactNode> = {
    notice: undefined,
    'already-hosting': <ButtonLink ref={noticeAction} href={returnTo} variant="secondary">Continue</ButtonLink>,
    profile: <ButtonLink ref={noticeAction} to="/settings" variant="secondary">Edit profile</ButtonLink>,
  }

  return (
    <PublicAuthLayout
      eyebrow="Your turn to host"
      title="Bring your event to life."
      description="One name for your events, cameras and live shows."
      backLink={{ to: '/', label: 'StreamBridge' }}
    >
      {hosting ? (
        <div className="space-y-5">
          <Notice ref={hostingNotice} tabIndex={-1} tone="success">
            {hosting.handle ? (
              <>
                You're hosting as <strong>@{hosting.handle}</strong>.
              </>
            ) : (
              "You're hosting."
            )}
          </Notice>
          <ButtonLink to={returnTo} replace className="w-full">
            Continue
            <ArrowRight aria-hidden="true" />
          </ButtonLink>
        </div>
      ) : (
        <form onSubmit={start} className="space-y-5" noValidate>
          {failure && (
            <Notice tone="error" alert action={noticeActions[failure.kind]}>
              {failure.message}
            </Notice>
          )}
          <TextField
            ref={nameInput}
            label="Organization name"
            description={NAME_RULE}
            id="organization-name"
            autoComplete="organization"
            maxLength={80}
            value={name}
            onChange={(event) => {
              setName(event.target.value)
              setNameError(null)
            }}
            error={nameError ?? undefined}
            required
            requiredMessage={NAME_MISSING}
            autoFocus
            disabled={busy}
          />
          <Button type="submit" className="w-full" loading={busy}>
            {busy ? 'Starting' : 'Start hosting'}
            <ArrowRight aria-hidden="true" />
          </Button>
        </form>
      )}
    </PublicAuthLayout>
  )
}
